import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { UserContext } from './UserContext';
import { axiosInstance } from '../utils/networking';

interface NavbarContextType {
  // The persistent sidebar column on /dashboard at >= 1024 px.
  isNavbarOpen: boolean;
  setIsNavbarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  // The temporary nav drawer (below 1024 px, and outside /dashboard).
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// DOM ids of the two navs the header menu button controls (aria-controls).
export const DASHBOARD_SIDEBAR_ID = 'dashboard-sidebar';
export const NAV_DRAWER_ID = 'nav-drawer';

// NavBar badge counts.
export interface NavCounts {
  openWebIntakes: number;
  validations: number;
}

interface NavCountsContextType {
  navCounts: NavCounts;
  // Every NavBar calls this on mount: each count is fetched once per page load
  // and signed-in user, and again after its request failed.
  requestNavCounts: () => void;
  // Dashboard_BFC calls this on mount: fetches the counts again, except one
  // that is still loading.
  refreshNavCounts: () => void;
}

const NavbarContext = createContext<NavbarContextType | undefined>(undefined);
// Kept apart from NavbarContext so the counts arriving re-render the NavBars
// and Dashboard_BFC only, not the Layout and page that read the open/closed
// state.
const NavCountsContext = createContext<NavCountsContextType | undefined>(
  undefined,
);

interface NavbarProviderProps {
  children: ReactNode;
}

export const NavbarProvider: React.FC<NavbarProviderProps> = ({ children }) => {
  const [isNavbarOpen, setIsNavbarOpen] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { user } = useContext(UserContext);
  const [navCounts, setNavCounts] = useState<NavCounts>({
    openWebIntakes: 0,
    validations: 0,
  });
  const userId = user?._id ? String(user._id) : null;
  // Per count URL: the user it is loaded or loading for, and the requests
  // still running.
  const countsUserRef = useRef<Record<string, string | null>>({});
  const loadingRef = useRef(new Set<string>());

  // A page can mount several NavBars (the dashboard sidebar, and the drawer's,
  // which remounts on every open), and /data_validation/count takes seconds,
  // so the counts are fetched once and shared. `force` fetches a loaded count
  // again; a request still running is never duplicated.
  const loadNavCounts = useCallback(
    (force: boolean) => {
      const fetchCount = (url: string, key: keyof NavCounts) => {
        if (loadingRef.current.has(url)) return;
        if (!force && countsUserRef.current[url] === userId) return;
        countsUserRef.current[url] = userId;
        loadingRef.current.add(url);
        axiosInstance
          .get(url)
          .then((res) => {
            if (res.status !== 200) throw new Error(`HTTP ${res.status}`);
            setNavCounts((counts) => ({ ...counts, [key]: res.data.count }));
          })
          .catch(() => {
            // Not cached, so the next NavBar to mount tries again.
            delete countsUserRef.current[url];
            console.log(`API Error at getting ${url}`);
          })
          .finally(() => loadingRef.current.delete(url));
      };
      fetchCount('/webintake/openCount', 'openWebIntakes');
      fetchCount('/data_validation/count', 'validations');
    },
    [userId],
  );
  const requestNavCounts = useCallback(
    () => loadNavCounts(false),
    [loadNavCounts],
  );
  const refreshNavCounts = useCallback(
    () => loadNavCounts(true),
    [loadNavCounts],
  );

  const navbar = useMemo(
    () => ({ isNavbarOpen, setIsNavbarOpen, isDrawerOpen, setIsDrawerOpen }),
    [isNavbarOpen, isDrawerOpen],
  );
  const counts = useMemo(
    () => ({ navCounts, requestNavCounts, refreshNavCounts }),
    [navCounts, requestNavCounts, refreshNavCounts],
  );

  return (
    <NavbarContext.Provider value={navbar}>
      <NavCountsContext.Provider value={counts}>
        {children}
      </NavCountsContext.Provider>
    </NavbarContext.Provider>
  );
};

export const useNavbarContext = (): NavbarContextType => {
  const context = useContext(NavbarContext);

  if (context === undefined) {
    throw new Error('useNavbarContext must be used within a NavbarProvider');
  }

  return context;
};

export const useNavCounts = (): NavCountsContextType => {
  const context = useContext(NavCountsContext);

  if (context === undefined) {
    throw new Error('useNavCounts must be used within a NavbarProvider');
  }

  return context;
};
